<template>
  <div class="w-full flex flex-col px-1 pt-1 pb-3 border border-border-normal" style="border-radius: 20px 20px 8px 8px;">
    <!-- Advertiser Selector (if multiple meta advertisers) -->
    <div v-if="brand?.id && advertiserOptions.length > 1" class="w-full flex items-center justify-between px-1.5 pt-1.5 pb-1">
      <AdvertiserSelectionDropdown
        class="fade-in z-30"
        no-button-shadow
        :selected-advertiser-id.sync="selectedAdvertiserId"
        :advertiser-options="advertiserOptions"
      />
      <button class="px-2 py-1 rounded-md transition-colors duration-100 hover:bg-neutral-25"
      @click="$emit('analyzeAdRanks')">
        <BaseText type="label" size="sm" class="text-text-muted">
          View All Rankings
        </BaseText>
      </button>
    </div>
    <!-- Header (Ranked Ads Category Selector) -->
    <div class="w-full flex items-center p-2 justify-between flex-wrap gap-y-2">
      <div class="flex items-center gap-2 flex-shrink-0">
        <BaseTooltipV2 v-for="option in rankedAdCategoryOptions" :key="`ranked-category-btn-${option.key}`"
        :tooltip-enabled="option.locked" text="Track to unlock" :offset="4" :delay="500">
          <button class="group flex items-center gap-1 p-1 rounded-md transition-colors duration-100"
          :class="option.locked ? 'opacity-50 pointer-events-none select-none' : option.isActive ? 'text-text-muted' : 'text-text-subdued hover:bg-neutral-25 hover:text-text-muted'"
          @click="toggleRankedAdsCategory(option.key)">
            <component :is="option.icon" class="transition-colors duration-100"
            :class="option.isActive ? 'text-icon-normal' : 'text-text-subdued group-hover:text-icon-normal'" />
            <BaseText type="label" size="sm">
              {{ option.label }}
            </BaseText>
            <BaseText v-if="rankedAdCounts.all > 0" type="label" size="xs" class="p-0.5 transition-colors duration-100 fade-in" style="border-radius: 20px; min-width: 20px;"
            :class="option.isActive ? 'text-text-normal bg-neutral-25' : 'text-text-subdued bg-neutral-25 group-hover:text-text-normal group-hover:bg-neutral-50'">
              {{ option.count }}
            </BaseText>
          </button>
        </BaseTooltipV2>
      </div>
      <div class="flex items-center gap-2">
        <!-- View All Rankings Button (show here if only one meta advertiser) -->
        <button v-if="advertiserOptions.length <= 1" class="px-2 py-1 rounded-md transition-colors duration-100 hover:bg-neutral-25"
        @click="$emit('analyzeAdRanks')">
          <BaseText type="label" size="sm" class="text-text-muted">
            View All Rankings
          </BaseText>
        </button>
        <!-- Scroll Buttons -->
        <div class="flex items-center flex-shrink-0">
          <button class="left-scroll p-1 rounded-l-md border border-border-normal transition-colors duration-100" :disabled="!leftScrollBtnEnabled"
          :class="leftScrollBtnEnabled ? 'text-text-muted hover:bg-neutral-50' : 'text-text-disabled cursor-default'"
          @click="handlePreviewScroll('left')">
            <svg ref="leftChevron" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M11.667 13.3337L8.92291 10.5896C8.59748 10.2641 8.59748 9.73651 8.92291 9.41107L11.667 6.66699" 
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="right-scroll p-1 rounded-r-md border-r border-t border-b border-border-normal transition-colors duration-100" 
          :disabled="!rightScrollBtnEnabled" :class="rightScrollBtnEnabled ? 'text-text-muted hover:bg-neutral-50' : 'text-text-disabled cursor-default'"
          @click="handlePreviewScroll('right')">
            <svg ref="rightChevron" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M8.33301 13.3337L11.0771 10.5896C11.4025 10.2641 11.4025 9.73651 11.0771 9.41107L8.33301 6.66699" 
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
    <!-- Ads Display -->
    <div class="relative w-full flex gap-x-1 p-1.5 rounded-2xl bg-neutral-25" style="height: 368px;">
      <div class="ranked-ads-scroll-container absolute top-1.5 -left-1 -right-1 -bottom-3 flex gap-x-1 min-w-0 px-2.5"
      ref="rankedAdsScrollContainer" infinite-wrapper :class="{ 'loading': loadingRankedAds }">
        <!-- Skeletons -->
        <template v-if="loadingRankedAds">
          <div v-for="i in 10" :key="`top-ranked-ad-skeleton-${i}`" style="width: 260px; height: 358px;"
          class="flex flex-col p-1 bg-white rounded-xl card-shadow flex-shrink-0">
            <div class="flex items-center gap-2 px-2 py-2.5">
              <div class="skeleton w-7 h-7 rounded-md" />
              <div class="skeleton w-32 h-5 rounded-md" />
            </div>
            <div class="w-full px-1"><div class="skeleton w-full rounded-md" style="height: 30px" /></div>
            <div class="skeleton w-full rounded-md mt-1.5" style="height: 260px;" />
          </div>
        </template>
        <div v-else-if="!rankedAdsList.length" class="w-full flex flex-col items-center justify-center">
          <TrophyIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-normal mt-3">
            No Rank Data Available
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal mt-1.5 text-center" style="max-width: 200px;">
            This page has no active ads that are currently ranked.
          </BaseText>
        </div>
        <!-- Ad Cards -->
        <template v-else>
          <BaseDynamicLoader v-for="(ad, index) in rankedAdsList" :key="`top-ranked-ad-${ad.id}-${index}`" :identifier="`top-ranked-ad-${ad.id}`" 
          observer-elm-selector=".ranked-ads-scroll-container" use-x-axis class="flex-shrink-0"
          :static-dimensions="{ width: '260px', height: '358px' }">
            <LibraryCard
              :advertisement="ad"
              :locked="locked"
              style="width: 260px; height: 358px;"
              discovery
              hide-creative-count
              show-spyder-ad-rank
              force-square-ratio
              force-disable-ad-copy
              forceDisableHeadlineCTA
              hide-save-button
              disable-brand-actions
              border-radius="12px"
              @openDetails="$emit('openAdDetails', ad)"
            />
          </BaseDynamicLoader>
          <HorizontalInfiniteLoading
            v-if="hasMoreRankedAds"
            ref="rankedAdsLoader"
            class="flex items-center flex-shrink-0"
            :identifier="infiniteId"
            force-use-infinite-wrapper
            @infinite="fetchMoreRankedAds"
          >
            <div slot="spinner">
              <BaseLoadingLogo :margin="2" />
            </div>
            <div slot="no-more" />
            <div slot="no-results" />
          </HorizontalInfiniteLoading>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import InfiniteLoading from 'vue-infinite-loading'
import { getBrandMetaAdvertiserOptions } from '@/utils/spyder/brandUtils'

import LibraryCard from '../../library/LibraryCard.vue'
import AdvertiserSelectionDropdown from '../AdvertiserSelectionDropdown.vue'

// Icons
import AdRankIcon from '../../globals/Icons/SpyderModeIcons/AdRankIcon.vue'
import EvergreenIcon from '../../globals/Icons/SpyderV2Icons/EvergreenIcon.vue'
import BreakoutIcon from '../../globals/Icons/SpyderV2Icons/BreakoutIcon.vue'
import TrophyIcon from '../../globals/Icons/TrophyIcon.vue'

const ADS_PER_PAGE = 10

// Vue Infinite loader override to measure horizontal distance instead of vertical
const HorizontalInfiniteLoading = {
  name: 'HorizontalInfiniteLoading',
  extends: InfiniteLoading,
  methods: {
    getCurrentDistance () {
      return this.$el.getBoundingClientRect().left - this.scrollParent.getBoundingClientRect().right
    }
  }
}

export default {
  name: 'AdRankPreview',
  components: {
    LibraryCard,
    AdvertiserSelectionDropdown,
    TrophyIcon,
    HorizontalInfiniteLoading
  },
  props: {
    brand: {
      required: true
    }
  },
  data () {
    return {
      selectedAdvertiserId: null,
      advertiserOptions: [],
      rankedAdsCategory: 'all',
      rankedAds: { all: [], evergreen: [], breakout: [] },
      rankedAdCounts: { all: 0, evergreen: 0, breakout: 0 },
      loadingRankedAds: true,
      leftScrollBtnEnabled: false,
      rightScrollBtnEnabled: true,
      latestRequestId: 0,
      infiniteId: +new Date()
    }
  },
  mounted () {
    const container = this.$refs.rankedAdsScrollContainer
    if (container) container.addEventListener('scroll', this.updateScrollButtons, { passive: true })
    window.addEventListener('resize', this.updateScrollButtons)
  },
  beforeDestroy () {
    const container = this.$refs.rankedAdsScrollContainer
    if (container) container.removeEventListener('scroll', this.updateScrollButtons)
    window.removeEventListener('resize', this.updateScrollButtons)
  },
  watch: {
    brand: {
      handler (newBrand) {
        if (!newBrand?.id) return
        this.initAdvertiserOptions()
      },
      immediate: true
    },
    // Watch both 'brand.id' and 'selectedAdvertiserId' together
    rankedAdsRequestKey: {
      handler () {
        this.resetRankedAdsState()
        this.fetchTopRankedAds('all')
        this.$nextTick(this.resetScrollPosition)
      },
      immediate: true
    }
  },
  computed: {
    locked () {
      const isSubscribed = this.brand?.is_subscribed
      return isSubscribed === undefined ? false : !isSubscribed
    },
    rankedAdsRequestKey () {
      return `${this.brand?.id}-${this.selectedAdvertiserId}`
    },
    rankedAdCategoryOptions () {
      return [
        {
          key: 'all',
          label: 'Top Ranked Ads',
          icon: AdRankIcon,
          isActive: this.rankedAdsCategory === 'all',
          locked: false,
          count: this.rankedAds.all.length,
        },
        ...(this.rankedAdCounts?.evergreen > 0 ? [{
          key: 'evergreen',
          label: 'Evergreen',
          icon: EvergreenIcon,
          isActive: this.rankedAdsCategory === 'evergreen',
          locked: !this.brand?.is_subscribed,
          count: this.rankedAdCounts.evergreen,
        }] : []),
        ...(this.rankedAdCounts?.breakout > 0 ? [{
          key: 'breakout',
          label: 'Breakout',
          icon: BreakoutIcon,
          isActive: this.rankedAdsCategory === 'breakout',
          locked: !this.brand?.is_subscribed,
          count: this.rankedAdCounts.breakout,
        }] : []),
      ]
    },
    rankedAdsList () {
      return this.rankedAds[this.rankedAdsCategory] || []
    },
    hasMoreRankedAds () {
      // 'all' is a fixed top ranked preview, so only the other categories paginate
      if (this.rankedAdsCategory === 'all') return false
      return this.rankedAdsList.length < this.rankedAdCounts[this.rankedAdsCategory]
    }
  },
  methods: {
    initAdvertiserOptions () {
      if (!this.brand?.id) return
      const metaAdvertiserOptions = getBrandMetaAdvertiserOptions(this.brand)
      this.advertiserOptions = metaAdvertiserOptions
      this.selectedAdvertiserId = metaAdvertiserOptions?.find(option => option.isPrimary)?.advertiserId
        ?? metaAdvertiserOptions?.[0]?.advertiserId ?? null
    },
    resetRankedAdsState () {
      this.rankedAdsCategory = 'all'
      this.rankedAds = { all: [], evergreen: [], breakout: [] }
      this.rankedAdCounts = { all: 0, evergreen: 0, breakout: 0 }
      this.infiniteId++
    },
    toggleRankedAdsCategory (category) {
      if (this.loadingRankedAds || category === this.rankedAdsCategory || this.locked) return
      this.rankedAdsCategory = category
      this.infiniteId++
      this.$nextTick(() => {
        this.updateScrollButtons()
        this.resetScrollPosition()
      })
      if (this.rankedAds[category].length) return
      this.fetchTopRankedAds(category)
    },
    async fetchTopRankedAds (category) {
      // Any in-flight request is now stale, since the advertiser or category it was made for has changed
      const requestId = ++this.latestRequestId
      // The brand is still resolving upstream, so hold the loading state rather than flashing an empty view
      if (!this.brand?.id ) return
      // Rankings are scoped to a single Meta advertiser, so we can't fetch them for brands without one
      if (!this.selectedAdvertiserId) {
        this.loadingRankedAds = false
        return
      }
      this.loadingRankedAds = true
      try {
        // When not subscribed, returns hard capped list of top 10 ads
        const { data: rankedAds, counts } = await ForeplayAPI.Brands.getBrandRankedAds(
          this.brand.id,
          this.selectedAdvertiserId,
          category,
          { limit: ADS_PER_PAGE, page: 1 }
        )
        if (requestId !== this.latestRequestId) return
        this.rankedAds[category] = rankedAds ?? []
        if (counts) this.rankedAdCounts = counts
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        console.error('Error fetching top ranked ads:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch top ranked ads. See console for details.'
        })
      } finally {
        if (requestId === this.latestRequestId) {
          this.loadingRankedAds = false
          this.$nextTick(this.updateScrollButtons)
        }
      }
    },
    async fetchMoreRankedAds ($state) {
      const category = this.rankedAdsCategory
      const infiniteId = this.infiniteId
      // Rounding up means a short page is never requested again, which would append duplicate ads
      const page = Math.ceil(this.rankedAds[category].length / ADS_PER_PAGE) + 1
      try {
        const { data: rankedAds } = await ForeplayAPI.Brands.getBrandRankedAds(
          this.brand.id,
          this.selectedAdvertiserId,
          category,
          { limit: ADS_PER_PAGE, page }
        )
        // The category or advertiser changed mid-request, and the loader has already been reset for the new list
        if (infiniteId !== this.infiniteId) return
        const ads = rankedAds ?? []
        this.rankedAds[category] = [...this.rankedAds[category], ...ads]
        if (ads.length < ADS_PER_PAGE || this.rankedAds[category].length >= this.rankedAdCounts[category]) {
          $state.complete()
        } else {
          $state.loaded()
        }
        this.$nextTick(this.updateScrollButtons)
      } catch (error) {
        if (infiniteId !== this.infiniteId) return
        console.error('Error fetching more ranked ads:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch more ranked ads. See console for details.'
        })
        $state.complete()
      }
    },
    updateScrollButtons () {
      const container = this.$refs.rankedAdsScrollContainer
      if (!container) return
      const { scrollLeft, clientWidth, scrollWidth } = container
      this.leftScrollBtnEnabled = scrollLeft > 1
      this.rightScrollBtnEnabled = scrollLeft < scrollWidth - clientWidth - 1
    },
    resetScrollPosition () {
      this.$refs?.rankedAdsScrollContainer?.scrollTo({ left: 0, behavior: 'instant' })
    },
    handlePreviewScroll (direction) {
      // Animate the chevron icon bounce
      const chevronIcon = direction === 'left' ? this.$refs.leftChevron : this.$refs.rightChevron
      const bounceAnimClass = direction === 'left' ? 'bounce-left-anim' : 'bounce-right-anim'
      chevronIcon.classList.add(bounceAnimClass)
      setTimeout(() => { chevronIcon.classList.remove(bounceAnimClass) }, 200)

      // Scroll the container
      const container = this.$refs.rankedAdsScrollContainer
      if (!container || this.loadingRankedAds) return

      const EDGE_GAP = 4
      const loaderEl = this.$refs.rankedAdsLoader?.$el
      const cards = Array.from(container.children).filter((child) => child !== loaderEl)
      const visibleLeft = container.scrollLeft
      const visibleRight = visibleLeft + container.clientWidth
      let target

      if (direction === 'right') {
        const candidates = cards.filter((card) => card.offsetLeft >= visibleRight)
        const secondCard = candidates[1]
        if (secondCard && secondCard !== cards[cards.length - 1]) {
          target = secondCard.offsetLeft + secondCard.offsetWidth - container.clientWidth + EDGE_GAP
        } else {
          target = container.scrollWidth - container.clientWidth
        }
      } else {
        const candidates = cards.filter((card) => card.offsetLeft + card.offsetWidth <= visibleLeft).reverse()
        const secondCard = candidates[1]
        if (secondCard && secondCard !== cards[0]) {
          target = secondCard.offsetLeft - EDGE_GAP
        } else {
          target = 0
        }
      }

      container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.ranked-ads-scroll-container {
  overflow-x: scroll;
}
.ranked-ads-scroll-container::-webkit-scrollbar {
  height: 12px;
  width: 12px;
}
.ranked-ads-scroll-container::-webkit-scrollbar-thumb {
  background-color: #DFE1E6;
  border-radius: 10px;
  border: 3px solid #ffffff;
  cursor: pointer;
}
.ranked-ads-scroll-container.loading::-webkit-scrollbar-thumb {
  background-color: #ffffff;
}
.ranked-ads-scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: #C1C7D0;
}
.card-shadow {
  box-shadow: 0px 0px 0px 1px rgba(24, 48, 123, 0.04), 0px 1px 1px -0.5px rgba(26, 48, 84, 0.04), 0px 2px 2px -1px rgba(26, 48, 84, 0.03), 0px 3px 3px -1.5px rgba(26, 48, 84, 0.03), 0px 5px 5px -2.5px rgba(26, 48, 84, 0.02), 0px 8px 8px -4px rgba(26, 48, 84, 0.02);
}

.bounce-left-anim {
  animation: bounceLeft 200ms ease-in-out;
}
.bounce-right-anim {
  animation: bounceRight 200ms ease-in-out;
}
@keyframes bounceRight {
  0% { transform: translateX(0); }
  50% { transform: translateX(2px); }
  100% { transform: translateX(0); }
}
@keyframes bounceLeft {
  0% { transform: translateX(0); }
  50% { transform: translateX(-2px); }
  100% { transform: translateX(0); }
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>